// Modules
import React, { Fragment, ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

// Styled Components
import * as HIGHLIGHT from "./highlight-event.styles";
import * as TYPO from "../../styles/common/typo.styles";
import { MarginTop } from "../../styles/common/space.styles";

// Types
import { EventProps, Event } from "../../types/events";
import { Store } from "../../types/store";

// Selectors
import { capitalizedLang } from "../../selectors/common.selector";

// Assets
import LeftArrow from "../../assets/left-arrow.svg";
import DateMonth from "../event/event.dateMonth.component";

const HighlightEvent = (): ReactElement => {
  // Dispatcher
  const lang = useSelector((state: any) => capitalizedLang()(state));
  const included = useSelector((state: any) => state.event.highlightIncluded);
  const dir = useSelector((state: any) => state.common.dir);
  const facets = useSelector((state: any) => state.facet);
  const history = useHistory();
  const params: any = useParams();
  const location: any = useLocation();
  // Selector
  const highlight: EventProps = useSelector(
    (state: Store) => state.event.highlight
  );
  const highlightId = useSelector(
    (state: any) => state.event.highlightId
  );
  const lists = useSelector((state: any) => state.event.list);
    
  // State
  const [event, setEvent] = useState<Event>({} as Event);
  const [hover, setHover] = useState(false);
  const [showHighlight, setShowHighlight] = useState(true);

  useEffect(() => {
    if (highlight && Object.keys(highlight).length) {
      setEvent(new Event({ ...highlight, lang, included, facets }));
    }

    const isEventInList = lists.some((event: any) =>
        event.attributes.drupal_internal__nid === highlightId
    );

    setShowHighlight(!isEventInList);
  }, [highlight, lists, highlightId]);

  const openUrl = (id: any) => {
    const currUrl = params.lang ? `/${params.lang}/event/${id}` : `/event/${id}`
    history.push(currUrl);
  };
  
  // Render
  if (highlight && !showHighlight)
    return (
      <HIGHLIGHT.Card
        tabIndex={1}
        onClick={() => openUrl(event.drupalInternalId)}
        onMouseLeave={() => setHover(false)}
        onMouseOver={() => setHover(true)}
        onKeyPress={(keyEvent: any) => {
          if (keyEvent.key === "Enter") {
            openUrl(event.drupalInternalId);
          }
        }}
      >
				<DateMonth event={event}/>
        <HIGHLIGHT.Details>
          <HIGHLIGHT.PBold>{event.type}</HIGHLIGHT.PBold>
          <MarginTop size={6} />
          <HIGHLIGHT.H2>{event.title}</HIGHLIGHT.H2>
          <MarginTop size={10} />
          <HIGHLIGHT.P>{event.subTitle}</HIGHLIGHT.P>
          <MarginTop size={20} />
          <HIGHLIGHT.P>{event.fullDate}</HIGHLIGHT.P>
          <MarginTop size={4} />
          <HIGHLIGHT.P>{event.hebrewDate}</HIGHLIGHT.P>
          <MarginTop size={4} />
          <HIGHLIGHT.P>{event.time}</HIGHLIGHT.P>
          <MarginTop size={16} />
          <HIGHLIGHT.TypeView>
            <HIGHLIGHT.PBoldXs> {event.cost}</HIGHLIGHT.PBoldXs>
            {event.inviteType && (
              <>
                <HIGHLIGHT.TextDivider>|</HIGHLIGHT.TextDivider>
                <HIGHLIGHT.PBoldXs> {event.inviteType}</HIGHLIGHT.PBoldXs>
              </>
            )}
          </HIGHLIGHT.TypeView>
          <HIGHLIGHT.LeftArrow direction={dir} hover={hover} src={LeftArrow} alt="Arrow"/>
        </HIGHLIGHT.Details>
        <HIGHLIGHT.Banner src={event.bannerUrl} alt="Banner"/>
      </HIGHLIGHT.Card>
    );

  return <Fragment />;
};

export default HighlightEvent;
